import React, { useState, useEffect } from "react";
import { withRouter, RouteComponentProps } from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import {
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import Container from "components/Layout/Container";
import Header from "components/Layout/Header";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import PublishButton from "components/PublishButton";
import AddDayForm from "./AddDayForm/AddDayForm";
import ROUTES from "navigation/routes";
import {
  SequenceDetailsCardWrapper,
  BackButtonWrapper,
  TextContainer,
} from "./styles";
import ConfirmationDialog from "components/ConfirmationDialog/ConfirmationDialog";
import SequenceDetailsCard from "./SequenceDetailsCard/SequenceDetailsCard";
import WorkoutSequenceStore from "stores/WorkoutSequenceStore";
import { SNACKBAR_TYPES } from "components/Snackbar/Snackbar";
import { observer } from "mobx-react";
import { getMessage } from "utils/messages";

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "react-beautiful-dnd";

import { applySnapshot, getSnapshot } from "mobx-state-tree";

type Props = GlobalPopupProps &
  SnackbarContextProps &
  RouteComponentProps<{ id: string }>;

const WorkoutSequenceDetails: React.FC<Props> = ({
  history,
  openPopup,
  setMessage,
  match,
}) => {
  const [sequence, setSequence] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPopupDirty, setIsPopupDirty] = useState(false);

  const fetchSequence = async () => {
    try {
      const id = Number(match.params.id);
      await WorkoutSequenceStore.fetchSequenceById(id);
      setSequence(WorkoutSequenceStore.currentSequence);
      setIsLoading(false);
    } catch (error) {
      console.error("Failed to fetch workout sequence:", error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSequence();

    return () => {
      WorkoutSequenceStore.clearCurrentSequence();
    };
  }, []);

  const onBackClick = () => {
    history.push(ROUTES.IN_APP_WORKOUT_SEQUENCES);
  };

  const onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    setIsPopupDirty(dirty);
  };

  const onAddWorkout = () => () => {
    openPopup({
      key: "AddWorkout",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <AddDayForm
          isLoading={false}
          onSubmit={onAddWorkoutSubmit(close)}
          onClose={close}
          onChangeIsDirty={onChangeIsPopupDirty}
        />
      ),
    });
  };

  const onAddWorkoutSubmit = (close: () => void) => async (values: any) => {
    const formattedValues = {
      day: values.day,
      workoutIds: values.workoutId?.map((w: any) => w.id) ?? [],
    };

    try {
      if (formattedValues.day && formattedValues.workoutIds.length) {
        const response = await WorkoutSequenceStore.addWorkoutSequenceDay(
          sequence.id,
          formattedValues,
        );

        if (response?.success) {
          fetchSequence();
          close();
        }
      }
    } catch (error) {
      setMessage(getMessage(error?.message), SNACKBAR_TYPES.ERROR);
    }
  };

  const onRemoveWorkout = (WSID: number, WDID: number, workout: any) => () => {
    openPopup({
      key: "RemoveWorkout",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title="Remove Workout"
          text={`Are you sure you want to remove ${workout.title} from ${sequence.title} sequence?`}
          declineText="Cancel"
          acceptText="Remove"
          onDecline={close}
          onAccept={onRemoveWorkoutSubmit(close, WSID, WDID)}
          isLoading={WorkoutSequenceStore.isLoading}
        />
      ),
    });
  };

  const onRemoveWorkoutSubmit = (
    close: () => void,
    WSID: any,
    WDID: number,
  ) => async () => {
    await WorkoutSequenceStore.deleteWorkoutSequenceDay(WSID, WDID);
    setIsLoading(true);
    await fetchSequence();
    setIsLoading(false);
    close();
  };

  const onDragEnd = async (result: DropResult) => {
    if (
      !result.destination ||
      !result.source ||
      result.source.index === result.destination.index ||
      result.destination.droppableId !== result.source.droppableId
    ) {
      return;
    }

    const day = Number(result.source.droppableId.replace("day-", ""));
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    const currentDayObj = sequence.days.find((d: any) => d.day === day);
    if (!currentDayObj) return;

    const prevSnapshot = getSnapshot(WorkoutSequenceStore.currentSequence);

    const updatedItems = [...currentDayObj.workoutSequenceDays];
    const [moved] = updatedItems.splice(sourceIndex, 1);
    updatedItems.splice(destIndex, 0, moved);

    const updatedSequence = {
      ...sequence,
      days: sequence.days.map((d: any) =>
        d.day === day ? { ...d, workoutSequenceDays: updatedItems } : d,
      ),
    };

    setSequence(updatedSequence);

    let moveType: "Top" | "Normal" = "Normal";
    let afterId = 0;

    if (destIndex === 0) {
      moveType = "Top";
    } else {
      moveType = "Normal";
      afterId = updatedItems[destIndex - 1].id;
    }

    try {
      await WorkoutSequenceStore.reorderWorkouts(sequence.id, day, {
        id: moved.id,
        afterId,
        moveType,
      });
    } catch (err) {
      console.error("Move failed, rolling back:", err);
      try {
        applySnapshot(WorkoutSequenceStore.currentSequence, prevSnapshot);
        setSequence(prevSnapshot);
      } catch (e) {
        console.error("Rollback failed:", e);
        await fetchSequence();
      }
    }
  };

  const workoutListIsEmpty = !sequence?.days?.length;
  return (
    <Container header={<Header controls={<PublishButton />} />}>
      <Page
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={onBackClick}
            >
              Back to Workout Sequences
            </Button>
          </BackButtonWrapper>
        }
        title="Edit Sequence Days"
        controls={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onAddWorkout()}
          >
            Add Day
          </Button>
        }
        isLoading={isLoading}
      >
        {sequence && (
          <TextContainer>
            <Typography variant="h2" color="textPrimary">
              {sequence.title}
            </Typography>

            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  checked={!sequence.showFirstWeekly}
                  disabled
                />
              }
              label="Show Sequence First"
            />
            <Typography
              variant="subtitle1"
              color="textSecondary"
              style={{ marginTop: -10, fontSize: 12 }}
            >
              If checked, sequences are shown first when weekly workouts are
              scheduled.
              <br />
              If unchecked, sequences are shown after the weekly workouts.
            </Typography>
          </TextContainer>
        )}

        {!workoutListIsEmpty ? (
          <DragDropContext onDragEnd={onDragEnd}>
            {sequence.days.map((dayGroup: any) => (
              <div key={dayGroup.day}>
                <Typography
                  variant="h3"
                  color="textSecondary"
                  style={{ marginTop: "20px" }}
                  gutterBottom
                >
                  Day {dayGroup.day}
                </Typography>

                <Droppable
                  droppableId={`day-${dayGroup.day}`}
                  type={`day-${dayGroup.day}`}
                >
                  {provided => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {dayGroup.workoutSequenceDays.map(
                        (wsd: any, index: number) => (
                          <Draggable
                            draggableId={String(wsd.id)}
                            index={index}
                            key={wsd.id}
                          >
                            {(provided, snapshot) => (
                              <SequenceDetailsCardWrapper
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                <SequenceDetailsCard
                                  id={wsd.id}
                                  day={dayGroup.day}
                                  title={wsd.workout.title}
                                  image={wsd.workout.imageUrl}
                                  onRemove={onRemoveWorkout(
                                    sequence.id,
                                    wsd.id,
                                    wsd.workout,
                                  )}
                                  dragHandleProps={
                                    provided.dragHandleProps ?? undefined
                                  }
                                  isDragging={snapshot.isDragging}
                                />
                              </SequenceDetailsCardWrapper>
                            )}
                          </Draggable>
                        ),
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </DragDropContext>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text="No days have been added for this sequence"
          />
        )}
      </Page>
    </Container>
  );
};

export default withGlobalPopup(
  withGlobalSnackbar(withRouter(observer(WorkoutSequenceDetails))),
);
